import ReactDOM from 'react-dom/client';
import { useEffect, useMemo, useState } from 'react';
import './index.css';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store/store';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DisplayPrivacyProvider } from './contexts/DisplayPrivacyContext';
import LoginScreen from './screens/LoginScreen';
import AnimatedAppRoutes from './components/AnimatedAppRoutes';
import LoadingSpinner from './components/LoadingSpinner';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { fetchAccounts } from './store/slices/accountsSlice';
import { fetchTags, fetchTagsByType } from './store/slices/tagsSlice';
import { fetchTransactionRange } from './store/slices/transactionsSlice';
import { getCurrentKolkataYearMonth } from './utils/dateTimeUtils';

function AppShell() {
  const { user, loading } = useAuth();
  const dispatch = useAppDispatch();
  const [authError, setAuthError] = useState<string | null>(null);
  const [shouldShowAuthSpinner, setShouldShowAuthSpinner] = useState(false);
  const [hasCompletedInitialHomeLoad, setHasCompletedInitialHomeLoad] = useState(false);
  const { status: accountsStatus } = useAppSelector((state) => state.accounts);
  const { status: tagsStatus, debitStatus, creditStatus } = useAppSelector((state) => state.tags);
  const { status: transactionsStatus, availableMonths } = useAppSelector((state) => state.transactions);

  const bootstrapRange = useMemo(() => {
    const currentYearMonth = getCurrentKolkataYearMonth();
    const startMonthIndex = currentYearMonth.year * 12 + (currentYearMonth.month - 1) - 2;

    return {
      startYear: Math.floor(startMonthIndex / 12),
      startMonth: (startMonthIndex % 12) + 1,
      endYear: currentYearMonth.year,
      endMonth: currentYearMonth.month,
    };
  }, []);

  const hasBootstrapRangeData = useMemo(() => {
    let current = new Date(bootstrapRange.startYear, bootstrapRange.startMonth - 1, 1);
    const end = new Date(bootstrapRange.endYear, bootstrapRange.endMonth - 1, 1);

    while (current <= end) {
      const year = String(current.getFullYear());
      const month = current.getMonth() + 1;
      if (!availableMonths[year]?.[month]) {
        return false;
      }
      current.setMonth(current.getMonth() + 1);
    }

    return true;
  }, [availableMonths, bootstrapRange]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('auth_error');
    if (error) {
      setAuthError(error);
    }

    if (params.has('auth_error') || params.has('auth_success')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setHasCompletedInitialHomeLoad(false);
      return;
    }

    if (accountsStatus === 'idle') {
      dispatch(fetchAccounts());
    }
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
    if (debitStatus === 'idle') {
      dispatch(fetchTagsByType('DEBIT'));
    }
    if (creditStatus === 'idle') {
      dispatch(fetchTagsByType('CREDIT'));
    }
  }, [user, accountsStatus, tagsStatus, debitStatus, creditStatus, dispatch]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (hasBootstrapRangeData) {
      return;
    }
    if (
      transactionsStatus === 'loading' ||
      transactionsStatus === 'loadingMore' ||
      transactionsStatus === 'failed'
    ) {
      return;
    }

    dispatch(fetchTransactionRange(bootstrapRange));
  }, [user, dispatch, hasBootstrapRangeData, bootstrapRange, transactionsStatus]);

  const isInitialHomeDataLoaded =
    accountsStatus === 'succeeded' &&
    tagsStatus === 'succeeded' &&
    debitStatus === 'succeeded' &&
    creditStatus === 'succeeded' &&
    hasBootstrapRangeData;

  useEffect(() => {
    if (isInitialHomeDataLoaded) {
      setHasCompletedInitialHomeLoad(true);
    }
  }, [isInitialHomeDataLoaded]);

  useEffect(() => {
    if (!loading) {
      setShouldShowAuthSpinner(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldShowAuthSpinner(true);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loading]);

  // not showing spinner on initial auth check to allow ios safari 
  // to settle the viewport dimesions and avoid janky spinner shft.
  if (loading) {
    if (!shouldShowAuthSpinner) {
      return <div className="fixed inset-0 bg-background text-foreground" />;
    }

    return (
      <div className="fixed inset-0 bg-background text-foreground flex items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!user) {
    return <LoginScreen errorMessage={authError} />;
  }

  const shouldShowInitialSpinner = !hasCompletedInitialHomeLoad && !isInitialHomeDataLoaded;
  if (shouldShowInitialSpinner) {
    return (
      <div className="fixed inset-0 bg-background text-foreground flex items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return <AnimatedAppRoutes />;
}

ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
).render(
  <Provider store={store}>
    <ThemeProvider>
      <DisplayPrivacyProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </AuthProvider>
      </DisplayPrivacyProvider>
    </ThemeProvider>
  </Provider>
);
